import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { useVehicleStore } from '../stores/vehicleStore'
import { useDriverStore } from '../stores/driverStore'
import { useScheduleStore } from '../stores/scheduleStore'
import { useAuthStore } from '../stores/authStore'

// Define route metadata for breadcrumbs and navigation
export interface RouteMeta extends Record<PropertyKey, unknown> {
  title: string
  breadcrumb?: string
  requiresData?: string[]
  icon?: string
  requiresAuth?: boolean
  requiresAdmin?: boolean
  public?: boolean
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: {
        title: 'เข้าสู่ระบบ',
        public: true
      } as RouteMeta
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/RegisterView.vue'),
      meta: {
        title: 'สมัครสมาชิก',
        public: true
      } as RouteMeta
    },
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        title: 'แดชบอร์ด',
        breadcrumb: 'หน้าหลัก',
        icon: 'home',
        requiresAuth: true,
        module: 'dashboard',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/vehicles',
      name: 'vehicles',
      component: () => import('../views/VehicleManagementView.vue'),
      meta: {
        title: 'จัดการยานพาหนะ',
        breadcrumb: 'ยานพาหนะ',
        requiresData: ['vehicles'],
        requiresAuth: true,
        icon: 'truck',
        module: 'vehicles',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/drivers',
      name: 'drivers',
      component: () => import('../views/DriverManagementView.vue'),
      meta: {
        title: 'จัดการคนขับ',
        breadcrumb: 'คนขับ',
        requiresData: ['drivers'],
        requiresAuth: true,
        icon: 'user',
        module: 'drivers',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/schedules',
      name: 'schedules',
      component: () => import('../views/ScheduleManagementView.vue'),
      meta: {
        title: 'จัดการตารางงาน',
        breadcrumb: 'ตารางงาน',
        requiresData: ['vehicles', 'drivers', 'schedules'],
        requiresAuth: true,
        icon: 'calendar',
        module: 'schedules',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/schedule-detail',
      name: 'schedule-detail',
      component: () => import('../views/PublicScheduleDetailView.vue'),
      meta: {
        title: 'รายละเอียดตารางงาน',
        breadcrumb: 'รายละเอียดตารางงาน',
        public: true,
        icon: 'calendar'
      } as RouteMeta
    },
    {
      path: '/driver-schedule-liff',
      name: 'driver-schedule-liff',
      component: () => import('../views/DriverScheduleLiffView.vue'),
      meta: {
        title: 'ตารางงานของคนขับ',
        breadcrumb: 'ตารางงานคนขับ',
        public: true,
        icon: 'calendar'
      } as RouteMeta
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('../views/ReportsView.vue'),
      meta: {
        title: 'รายงานและการวิเคราะห์',
        breadcrumb: 'รายงาน',
        requiresData: ['vehicles', 'drivers', 'schedules'],
        requiresAuth: true,
        icon: 'chart',
        module: 'reports',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/driving-hours',
      name: 'driving-hours',
      component: () => import('../views/DrivingHoursDashboardView.vue'),
      meta: {
        title: 'แดชบอร์ดชั่วโมงขับรถ',
        breadcrumb: 'ชั่วโมงขับรถ',
        requiresData: ['drivers', 'schedules'],
        requiresAuth: true,
        icon: 'clock',
        module: 'reports',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('../views/NotificationManagementView.vue'),
      meta: {
        title: 'จัดการการแจ้งเตือน',
        breadcrumb: 'การแจ้งเตือน',
        requiresData: ['drivers'],
        requiresAuth: true,
        icon: 'bell',
        module: 'notifications',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/UserManagementView.vue'),
      meta: {
        title: 'จัดการสมาชิก',
        breadcrumb: 'สมาชิก',
        requiresAuth: true,
        icon: 'users',
        module: 'users',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/notiflix-demo',
      name: 'notiflix-demo',
      component: () => import('../components/NotiflixDemo.vue'),
      meta: {
        title: 'ทดสอบ Notiflix',
        breadcrumb: 'ทดสอบ Notiflix',
        icon: 'bell'
      } as RouteMeta
    },
    {
      path: '/thai-datetime-demo',
      name: 'thai-datetime-demo',
      component: () => import('../components/ThaiDateTimeDemo.vue'),
      meta: {
        title: 'ทดสอบวันที่ไทย',
        breadcrumb: 'ทดสอบวันที่ไทย',
        icon: 'clock'
      } as RouteMeta
    },
    {
      path: '/test-thai-date',
      name: 'test-thai-date',
      component: () => import('../components/TestThaiDate.vue'),
      meta: {
        title: 'ทดสอบวันที่ไทย',
        breadcrumb: 'ทดสอบวันที่ไทย',
        icon: 'clock'
      } as RouteMeta
    },
    {
      path: '/simple-thai-test',
      name: 'simple-thai-test',
      component: () => import('../components/SimpleThaiDateTest.vue'),
      meta: {
        title: 'ทดสอบวันที่ไทยแบบง่าย',
        breadcrumb: 'ทดสอบวันที่ไทยแบบง่าย',
        icon: 'clock'
      } as RouteMeta
    },
    {
      path: '/line-notification-test',
      name: 'line-notification-test',
      component: () => import('../views/LineNotificationTestView.vue'),
      meta: {
        title: 'ทดสอบ LINE Notification',
        breadcrumb: 'ทดสอบ LINE Notification',
        icon: 'flask'
      } as RouteMeta
    },

    {
      path: '/settings',
      name: 'settings',
      redirect: '/settings/main',
      meta: {
        title: 'การตั้งค่าระบบ',
        breadcrumb: 'การตั้งค่า',
        icon: 'cog',
        module: 'settings',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/settings/main',
      name: 'settings-main',
      component: () => import('../views/SettingsView.vue'),
      meta: {
        title: 'การตั้งค่าหลัก',
        breadcrumb: 'การตั้งค่า',
        icon: 'cog',
        module: 'settings',
        requiredAction: 'view'
      } as RouteMeta
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: '/'
    }
  ]
})

// Navigation guard for authentication and data loading
router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next) => {
  const meta = to.meta as RouteMeta
  const authStore = useAuthStore()

  // Always check auth
  if (!authStore.user) {
    await authStore.checkAuth()
  }

  // Debug: Log current user
  if (import.meta.env.DEV) {
    console.log('🔐 Router Guard:', {
      to: to.path,
      user: authStore.user?.email,
      role: authStore.user?.role,
      isAuthenticated: authStore.isAuthenticated,
      requiresAdmin: meta?.requiresAdmin,
      module: meta?.module,
      requiredAction: meta?.requiredAction
    })
  }

  // Set document title
  if (meta?.title) {
    document.title = `${meta.title} - ระบบจัดการยานพาหนะและคนขับ`
  }

  // Check if route requires authentication
  if (meta?.requiresAuth && !authStore.isAuthenticated) {
    console.warn('Access denied: Authentication required')
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  // Check if route requires admin
  if (meta?.requiresAdmin && authStore.user?.role !== 'admin') {
    console.warn('Access denied: Admin only route')
    next({ name: 'home' })
    return
  }

  // Redirect to home if already authenticated and trying to access login/register pages
  if (meta?.public && authStore.isAuthenticated && (to.name === 'login' || to.name === 'register')) {
    next({ name: 'home' })
    return
  }

  // Load required data based on route metadata
  if (meta?.requiresData) {
    try {
      const promises: Promise<void>[] = []

      if (meta.requiresData.includes('vehicles')) {
        const vehicleStore = useVehicleStore()
        if (vehicleStore.vehicles.length === 0 && !vehicleStore.isLoading) {
          promises.push(vehicleStore.fetchAllVehicles())
        }
      }

      if (meta.requiresData.includes('drivers')) {
        const driverStore = useDriverStore()
        if (driverStore.drivers.length === 0 && !driverStore.isLoading) {
          promises.push(driverStore.fetchAllDrivers())
        }
      }

      if (meta.requiresData.includes('schedules')) {
        const scheduleStore = useScheduleStore()
        if (scheduleStore.scheduleEntries.length === 0 && !scheduleStore.isLoading) {
          promises.push(scheduleStore.fetchAllScheduleEntries())
        }
      }

      // Wait for all required data to load
      await Promise.all(promises)
    } catch (error) {
      console.error('Error loading route data:', error)
      // Continue navigation even if data loading fails
    }
  }

  next()
})

// Generate breadcrumb trail for current route
export function generateBreadcrumbs(currentRoute: RouteLocationNormalized) {
  const breadcrumbs = [
    { name: 'หน้าหลัก', path: '/', current: false }
  ]
  
  const meta = currentRoute.meta as RouteMeta
  if (meta?.breadcrumb && currentRoute.name !== 'home') {
    breadcrumbs.push({
      name: meta.breadcrumb,
      path: currentRoute.path,
      current: true
    })
  }
  
  return breadcrumbs
}

export default router